import { EntityLinks, Goal, GoalTreeIndex, ID } from '@griply/types';

import { getGoalParentIds } from './get-goal-parents';

export type GetSubGoalsOptions = {
  goals: Goal[];
  recursive?: boolean;
  links?: EntityLinks;
  index?: GoalTreeIndex;
  // Callers looping over goals should build this once — rebuilding it per call is what makes the callers O(n²).
  parentToChildrenMap?: Map<ID, Goal[]>;
};

// A goal is a child of EVERY parent it links to, so it appears in each of their buckets.
export const buildParentToChildrenMap = <TGoal extends Goal>(
  goals: TGoal[],
  links?: EntityLinks,
): Map<ID, TGoal[]> => {
  const map = new Map<ID, TGoal[]>();

  goals.forEach((goal) => {
    getGoalParentIds(goal, { links }).forEach((parentId) => {
      const existing = map.get(parentId) ?? [];
      existing.push(goal);
      map.set(parentId, existing);
    });
  });

  return map;
};

export const getSubGoals = (
  id: ID | undefined,
  {
    goals,
    recursive = false,
    links,
    index,
    parentToChildrenMap: providedMap,
  }: GetSubGoalsOptions,
): Goal[] => {
  if (!id) {
    return [];
  }

  const parentToChildrenMap =
    providedMap ??
    index?.childrenByParentId ??
    buildParentToChildrenMap(goals, links);

  // The index's arrays outlive this call, so hand back a copy the caller may own.
  const directSubGoals = [...(parentToChildrenMap.get(id) || [])];

  if (!recursive) {
    return directSubGoals;
  }

  const processedIds = new Set<ID>(directSubGoals.map((goal) => goal.id));

  const collectDescendants = (currentLevel: Goal[]): Goal[] => {
    if (!currentLevel.length) {
      return [];
    }

    const nextLevel = currentLevel.flatMap((goal) =>
      (parentToChildrenMap.get(goal.id) || []).filter((child) => {
        if (processedIds.has(child.id)) {
          return false;
        }
        processedIds.add(child.id);
        return true;
      }),
    );

    return [...nextLevel, ...collectDescendants(nextLevel)];
  };

  return [...directSubGoals, ...collectDescendants(directSubGoals)];
};
